import { CheckIcon, ChevronLeftIcon, ChevronRightIcon, TriangleDownIcon, TriangleUpIcon } from '@radix-ui/react-icons'
import { Label } from '@radix-ui/react-label'
import * as Select from '@radix-ui/react-select'
import classnames from 'classnames'
import React from 'react'
import { typography } from '../../main.css.ts'
import ChevronBarLeftIcon from '../icons/chevron-bar-left.svg?react'
import ChevronBarRightIcon from '../icons/chevron-bar-right.svg?react'
import * as styles from './pagination-controls.css.ts'

export type PaginationControlsProps = {
  tableLabel: string
  pageIndex: number
  pageSize: number
  rowCount: number
  setPageIndex: (pageIndex: number) => void
  setPageSize: (pageSize: number) => void
}

/**
 * @param tableLabel Label for the table, used in aria-label
 * @param pageIndex Current page index (zero indexed)
 * @param pageSize Current number of items per page
 * @param rowCount Total number of rows in the table
 * @param setPageIndex State setter for page index (allow controls to create the handlers)
 * @param setPageSize State setter for page size (allow controls to create the handlers)
 */
export const PaginationControls: React.FC<PaginationControlsProps> = ({ tableLabel, pageIndex, pageSize, rowCount, setPageIndex, setPageSize }) => {
  if (rowCount === 0) return <></>

  const { rowMin, rowMax, isPreviousPageButtonActive, isNextPageButtonActive } = calculatePaginationControls(pageSize, rowCount, pageIndex)

  const onFirstPage = () => setPageIndex(0)
  const onPreviousPage = () => setPageIndex(pageIndex - 1)
  const onNextPage = () => setPageIndex(pageIndex + 1)
  const onLastPage = () => setPageIndex(Math.floor(rowCount / pageSize))
  const onChangePageSize = (pageSize: string) => {
    onFirstPage()
    setPageSize(Number(pageSize))
  }

  return (
    <nav aria-label={`${tableLabel} Table Pagination Navigation`} className={styles.paginationControls}>
      <div style={{ display: 'flex', gap: '8px', alignItems: 'baseline' }}>
        <button aria-label={'Go to First Page'} onClick={onFirstPage} disabled={!isPreviousPageButtonActive}>
          <ChevronBarLeftIcon className={styles.endChevron[isPreviousPageButtonActive ? 'active' : 'disabled']} />
        </button>
        <button onClick={onPreviousPage} disabled={!isPreviousPageButtonActive} aria-label={`Go to Previous Page`}>
          <ChevronLeftIcon className={styles.baseChevron[isPreviousPageButtonActive ? 'active' : 'disabled']} />
        </button>
        <span className={typography.smallBold}>
          {rowMin}-{rowMax}
        </span>{' '}
        <span className={typography.extraSmall}>of</span> <span className={typography.small}>{rowCount}</span>
        <button onClick={onNextPage} disabled={!isNextPageButtonActive} aria-label={'Go to Next Page'}>
          <ChevronRightIcon className={styles.baseChevron[isNextPageButtonActive ? 'active' : 'disabled']} />
        </button>
        <button aria-label={'Go to Last Page'} onClick={onLastPage} disabled={!isNextPageButtonActive}>
          <ChevronBarRightIcon className={styles.endChevron[isNextPageButtonActive ? 'active' : 'disabled']} />
        </button>
      </div>
      <div>
        <Select.Root defaultValue={String(pageSize)} onValueChange={onChangePageSize}>
          <Select.Trigger
            className={styles.selectTrigger}
            aria-label={`Select ${tableLabel} Page Size`}
            id={`${tableLabel.toLowerCase()}-page-size-select`}
          >
            <Select.Value />
            <Select.Icon>
              <TriangleDownIcon className={styles.chevronDown} />
              <TriangleUpIcon className={styles.chevronUp} />
            </Select.Icon>
          </Select.Trigger>
          <Select.Content
            className={styles.selectContent}
            onCloseAutoFocus={e => {
              e.preventDefault()
            }}
            position="popper"
            side="bottom"
            avoidCollisions={false}
          >
            <SelectItem value={'10'} text="10" />
            <SelectItem value={'25'} text="25" />
            <SelectItem value={'50'} text="50" />
          </Select.Content>
        </Select.Root>
        <Label className={styles.label} aria-label={`${pageSize} per page`} htmlFor={`${tableLabel.toLowerCase()}-page-size-select`}>
          per page
        </Label>
      </div>
    </nav>
  )
}

type SelectItemProps = {
  children?: React.ReactNode
  text: string
  className?: string
  value: string
  disabled?: boolean
}

const SelectItem = React.forwardRef<HTMLDivElement, SelectItemProps>(({ children, text, className, value, ...props }, forwardedRef) => {
  return (
    <Select.Item className={classnames(styles.selectItem, className)} value={value} {...props} ref={forwardedRef}>
      {children}
      <Select.ItemText>{text}</Select.ItemText>
      <Select.ItemIndicator className={styles.selectItemIndicator}>
        <CheckIcon className={styles.checkIcon} />
      </Select.ItemIndicator>
    </Select.Item>
  )
})

export const calculatePaginationControls = (pageSize: number, rowCount: number, pageIndex: number) => ({
  totalPages: Math.ceil(rowCount / pageSize) - 1,
  rowMin: (pageIndex + 1) * pageSize - pageSize + 1,
  rowMax: Math.min((pageIndex + 1) * pageSize, rowCount),
  isPreviousPageButtonActive: pageIndex > 0,
  isNextPageButtonActive: pageIndex < Math.ceil(rowCount / pageSize) - 1,
})
